import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  LinearProgress,
  Alert,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip,
  Paper,
  Divider
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
  Description as FileIcon
} from '@mui/icons-material';
import axios from 'axios';

const BulkUploadDialog = ({ open, onClose, onSuccess }) => {
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [results, setResults] = useState([]);
  const [error, setError] = useState('');

  const handleFileSelect = (event) => {
    const selectedFiles = Array.from(event.target.files);
    const docxFiles = selectedFiles.filter(file => 
      file.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
    );
    
    if (docxFiles.length !== selectedFiles.length) {
      setError('Only .docx files are supported. Some files were filtered out.');
    } else {
      setError('');
    }
    
    setFiles(docxFiles);
    setResults([]);
  };

  const handleUpload = async () => {
    if (files.length === 0) {
      setError('Please select at least one .docx file');
      return;
    }

    setUploading(true);
    setUploadProgress(0);
    setError('');
    const uploadResults = [];

    try {
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const formData = new FormData();
        formData.append('docxFile', file);
        
        try {
          const response = await axios.post('/api/stories/admin/bulk-upload', formData, {
            headers: {
              'Content-Type': 'multipart/form-data',
            },
          });
          
          uploadResults.push({
            filename: file.name,
            status: 'success',
            message: response.data.message,
            storyId: response.data.storyId
          });
        } catch (fileError) {
          uploadResults.push({
            filename: file.name,
            status: 'error',
            message: fileError.response?.data?.message || 'Upload failed'
          });
        }
        
        setUploadProgress(((i + 1) / files.length) * 100);
      }
      
      setResults(uploadResults);
      
      // If any uploads were successful, refresh the stories list
      if (uploadResults.some(r => r.status === 'success')) {
        onSuccess();
      }
      
    } catch (error) {
      setError('Upload process failed: ' + error.message);
    } finally {
      setUploading(false);
    }
  };

  const handleClose = () => {
    if (!uploading) {
      setFiles([]);
      setResults([]);
      setError('');
      setUploadProgress(0);
      onClose();
    }
  };

  const getFileSize = (bytes) => {
    const mb = bytes / (1024 * 1024);
    return `${mb.toFixed(1)} MB`;
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <UploadIcon />
          Bulk Upload Stories from .docx Files
        </Box>
      </DialogTitle>
      
      <DialogContent>
        <Box mb={3}>
          <Typography variant="body2" color="text.secondary" paragraph>
            Upload multiple .docx files to automatically create stories. Each file will be converted to a new story using the filename as the title.
          </Typography>
          
          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="body2">
              <strong>Requirements:</strong>
              <br />• Only .docx files are supported
              <br />• Each file will create a new story as a draft
              <br />• File content will be converted to HTML automatically
              <br />• You can edit titles, content, and publish settings after upload
            </Typography>
          </Alert>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        <Box mb={3}>
          <input
            type="file"
            multiple
            accept=".docx"
            onChange={handleFileSelect}
            style={{ display: 'none' }}
            id="bulk-upload-input"
            disabled={uploading}
          />
          <label htmlFor="bulk-upload-input">
            <Button
              variant="outlined"
              component="span"
              startIcon={<UploadIcon />}
              disabled={uploading}
              fullWidth
              sx={{ py: 2 }}
            >
              Select .docx Files
            </Button>
          </label>
        </Box>

        {files.length > 0 && (
          <Paper variant="outlined" sx={{ mb: 3 }}>
            <Box p={2}>
              <Typography variant="h6" gutterBottom>
                Selected Files ({files.length})
              </Typography>
              <List dense>
                {files.map((file, index) => (
                  <ListItem key={index}>
                    <ListItemIcon>
                      <FileIcon />
                    </ListItemIcon>
                    <ListItemText
                      primary={file.name}
                      secondary={`Size: ${getFileSize(file.size)}`}
                    />
                  </ListItem>
                ))}
              </List>
            </Box>
          </Paper>
        )}

        {uploading && (
          <Box mb={3}>
            <Typography variant="body2" gutterBottom>
              Uploading files... {Math.round(uploadProgress)}%
            </Typography>
            <LinearProgress variant="determinate" value={uploadProgress} />
          </Box>
        )}

        {results.length > 0 && (
          <Paper variant="outlined">
            <Box p={2}>
              <Typography variant="h6" gutterBottom>
                Upload Results
              </Typography>
              <List dense>
                {results.map((result, index) => (
                  <React.Fragment key={index}>
                    <ListItem>
                      <ListItemIcon>
                        {result.status === 'success' ? (
                          <SuccessIcon color="success" />
                        ) : (
                          <ErrorIcon color="error" />
                        )}
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Box display="flex" alignItems="center" gap={1}>
                            {result.filename}
                            <Chip
                              label={result.status}
                              color={result.status === 'success' ? 'success' : 'error'}
                              size="small"
                            />
                          </Box>
                        }
                        secondary={result.message}
                      />
                    </ListItem>
                    {index < results.length - 1 && <Divider />}
                  </React.Fragment>
                ))}
              </List>
            </Box>
          </Paper>
        )}
      </DialogContent>
      
      <DialogActions>
        <Button onClick={handleClose} disabled={uploading}>
          {results.length > 0 ? 'Close' : 'Cancel'}
        </Button>
        {files.length > 0 && results.length === 0 && (
          <Button
            onClick={handleUpload}
            variant="contained"
            disabled={uploading}
            startIcon={<UploadIcon />}
          >
            {uploading ? 'Uploading...' : `Upload ${files.length} File${files.length !== 1 ? 's' : ''}`}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default BulkUploadDialog;